/* STTR theme runtime — vanilla ports of the React behaviour (no libraries).
   Each module boots from a data attribute and is a no-op when absent, so this one
   deferred file serves every template. Everything re-scans on
   `shopify:section:load` so the theme editor previews behaviour live. */
(() => {
  "use strict";

  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const pad = (n) => String(n).padStart(2, "0");
  const routes = (window.STTR && window.STTR.routes) || {
    cart: "/cart", cartAdd: "/cart/add", cartChange: "/cart/change", predictiveSearch: "/search/suggest", search: "/search", products: "/collections/all",
  };
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  const wideWords = String((window.STTR && window.STTR.wideKeywords) || "").toLowerCase().split(",").map((w) => w.trim()).filter(Boolean);
  const jewelClass = (title) => (wideWords.some((w) => String(title).toLowerCase().includes(w)) ? "jewel jewel--wide" : "jewel");

  const storage = {
    get(key, fallback) {
      try { const v = JSON.parse(localStorage.getItem(key)); return v == null ? fallback : v; } catch { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ }
    },
  };

  /* Shopify money format → string, e.g. "${{amount}} CAD" */
  function money(cents) {
    const format = (window.STTR && window.STTR.moneyFormat) || "${{amount}}";
    const amount = Number(cents) / 100;
    const fixed = (d, thousands = ",", decimal = ".") => {
      const [int, frac] = amount.toFixed(d).split(".");
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? grouped + decimal + frac : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case "amount_no_decimals": return fixed(0);
        case "amount_with_comma_separator": return fixed(2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return fixed(0, ".");
        case "amount_with_space_separator": return fixed(2, " ", ",");
        case "amount_no_decimals_with_space_separator": return fixed(0, " ");
        case "amount_with_apostrophe_separator": return fixed(2, "'");
        default: return fixed(2);
      }
    });
  }

  /* ---------------------------------------------------------------- cart --- */
  function setCartCount(n) {
    $$("[data-sttr-cart-count]").forEach((el) => { el.textContent = `(${n})`; });
    document.dispatchEvent(new CustomEvent("sttr:cart", { detail: { item_count: n } }));
  }
  async function refreshCartCount() {
    try {
      const res = await fetch(`${routes.cart}.js`, { headers: { Accept: "application/json" } });
      const cart = await res.json();
      setCartCount(cart.item_count);
    } catch { /* offline */ }
  }
  async function cartAdd(body) {
    const init = body instanceof FormData
      ? { method: "POST", body, headers: { Accept: "application/json" } }
      : { method: "POST", body: JSON.stringify(body), headers: { Accept: "application/json", "Content-Type": "application/json" } };
    const res = await fetch(`${routes.cartAdd}.js`, init);
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || "Could not add to bag");
    await refreshCartCount();
    return json;
  }
  function flash(button, text, ms = 1800) {
    const original = button.dataset.label || button.textContent;
    button.textContent = text;
    clearTimeout(button._sttrFlash);
    button._sttrFlash = setTimeout(() => { button.textContent = original; }, ms);
  }
  function initQuickAdd(scope) {
    $$("[data-sttr-add]", scope).forEach((button) => {
      if (button.dataset.init) return;
      button.dataset.init = "1";
      button.addEventListener("click", async () => {
        button.disabled = true;
        try {
          await cartAdd({ items: [{ id: Number(button.dataset.sttrAdd), quantity: 1 }] });
          flash(button, button.dataset.done || "Added");
        } catch (err) {
          flash(button, err.message || "Try again", 2600);
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  /* -------------------------------------------------------------- header --- */
  function initHeader(header) {
    if (header.dataset.init) return;
    header.dataset.init = "1";
    const overlay = header.dataset.overlay === "true";
    const burger = $("[data-sttr-burger]", header);
    const phone = $("[data-sttr-phone]", header);
    const mega = $("[data-sttr-mega]", header);
    const search = $("[data-sttr-search]", header);
    let open = false;

    const applySolid = () => header.classList.toggle("is-solid", !overlay || (window.scrollY || document.documentElement.scrollTop) > 8);
    applySolid();
    window.addEventListener("scroll", applySolid, { passive: true });

    const setOpen = (next) => {
      open = next;
      header.classList.toggle("is-open", open);
      if (burger) {
        burger.classList.toggle("is-open", open);
        burger.setAttribute("aria-expanded", String(open));
        burger.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      }
      if (phone) {
        phone.classList.toggle("is-open", open);
        phone.setAttribute("aria-hidden", String(!open));
      }
      document.body.style.overflow = open ? "hidden" : "";
    };
    if (burger) burger.addEventListener("click", (e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); });
    if (phone) $$("a", phone).forEach((a) => a.addEventListener("click", () => setOpen(false)));

    const shopToggle = $("[data-sttr-shop-toggle]", header);
    const shopSub = $("[data-sttr-shop-sub]", header);
    if (shopToggle && shopSub) {
      shopToggle.addEventListener("click", () => {
        const next = !shopSub.classList.contains("is-open");
        shopSub.classList.toggle("is-open", next);
        shopToggle.setAttribute("aria-expanded", String(next));
        const sign = $("[data-sttr-shop-sign]", shopToggle);
        if (sign) sign.textContent = next ? "–" : "+";
      });
    }

    if (mega) {
      const setMega = (next) => mega.classList.toggle("is-open", next);
      $$("[data-sttr-mega-trigger]", header).forEach((a) => a.addEventListener("mouseenter", () => setMega(true)));
      $$("[data-sttr-mega-close]", header).forEach((a) => a.addEventListener("mouseenter", () => setMega(false)));
      mega.addEventListener("mouseenter", () => setMega(true));
      header.addEventListener("mouseleave", () => setMega(false));
      $$("[data-sttr-mega-trigger]", header).forEach((a) => {
        a.addEventListener("focus", () => setMega(true));
        a.addEventListener("keydown", (e) => { if (e.key === "Escape") setMega(false); });
      });
    }

    const account = $("[data-sttr-account]", header);
    const accountToggle = $("[data-sttr-account-toggle]", header);
    if (account && accountToggle) {
      const setAccount = (next) => {
        account.classList.toggle("is-open", next);
        account.setAttribute("aria-hidden", String(!next));
        accountToggle.setAttribute("aria-expanded", String(next));
      };
      accountToggle.addEventListener("click", (e) => { e.preventDefault(); setAccount(!account.classList.contains("is-open")); });
      document.addEventListener("click", (e) => { if (!account.contains(e.target) && !accountToggle.contains(e.target)) setAccount(false); });
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") setAccount(false); });
    }

    if (search) {
      const input = $("[data-sttr-search-input]", search);
      const results = $("[data-sttr-search-results]", search);
      const empty = $("[data-sttr-search-empty]", search);
      const initial = results ? results.innerHTML : "";
      let timer = 0;
      let seq = 0;
      const setSearch = (next) => {
        search.classList.toggle("is-open", next);
        search.setAttribute("aria-hidden", String(!next));
        if (next && input) setTimeout(() => input.focus(), 30);
        if (!next && input) { input.value = ""; if (results) results.innerHTML = initial; if (empty) empty.classList.add("st:hidden"); }
      };
      $$("[data-sttr-search-open]", header).forEach((b) => b.addEventListener("click", () => setSearch(true)));
      $$("[data-sttr-search-close]", header).forEach((b) => b.addEventListener("click", () => setSearch(false)));
      document.addEventListener("keydown", (e) => { if (e.key === "Escape" && search.classList.contains("is-open")) setSearch(false); });
      const render = (products) => {
        if (!results) return;
        if (!products.length) {
          results.innerHTML = "";
          if (empty) empty.classList.remove("st:hidden");
          return;
        }
        if (empty) empty.classList.add("st:hidden");
        results.innerHTML = products.map((p) => {
          const img = p.featured_image && p.featured_image.url
            ? `<img src="${p.featured_image.url}${p.featured_image.url.includes("?") ? "&" : "?"}width=120" alt="" width="48" height="56" class="${jewelClass(p.title)} st:h-full st:w-full st:object-cover st:object-center" loading="lazy">`
            : "";
          return `<li><a href="${p.url}" class="st:flex st:items-center st:gap-3 st:border-b st:border-line st:py-3"><span class="st:block st:h-14 st:w-12 st:shrink-0 st:overflow-hidden st:bg-black">${img}</span><span>${escapeHtml(p.title)}</span></a></li>`;
        }).join("");
      };
      if (input) {
        input.addEventListener("input", () => {
          clearTimeout(timer);
          const q = input.value.trim();
          if (!q) { if (results) results.innerHTML = initial; if (empty) empty.classList.add("st:hidden"); return; }
          timer = setTimeout(async () => {
            const mine = ++seq;
            try {
              const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][fields]=title,product_type,body`;
              const res = await fetch(url, { headers: { Accept: "application/json" } });
              const json = await res.json();
              if (mine !== seq) return;
              render((json.resources && json.resources.results && json.resources.results.products) || []);
            } catch { /* keep the previous list */ }
          }, 180);
        });
      }
    }
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  /* ------------------------------------------------------------- reveals --- */
  const revealObserver = "IntersectionObserver" in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove("is-armed");
        entry.target.classList.add("is-in");
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.18 })
    : null;
  function initReveals(scope) {
    $$(".reveal", scope).forEach((el) => {
      if (el.dataset.revealInit) return;
      el.dataset.revealInit = "1";
      if (reduce() || !revealObserver) { el.classList.add("is-in"); return; }
      const r = el.getBoundingClientRect();
      const seen = r.top < window.innerHeight * 0.9 && r.bottom > 40;
      if (!seen) el.classList.add("is-armed");
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------- tilt --- */
  const fine = window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)");
  function initTilt(scope) {
    $$("[data-sttr-tilt]", scope).forEach((el) => {
      if (el.dataset.init) return;
      el.dataset.init = "1";
      const max = Number(el.dataset.max || 7);
      let frame = 0;
      const reset = () => {
        el.style.setProperty("--rx", "0deg"); el.style.setProperty("--ry", "0deg");
        el.style.setProperty("--gx", "50%"); el.style.setProperty("--gy", "50%");
      };
      el.addEventListener("pointermove", (e) => {
        if (!fine.matches || e.pointerType !== "mouse") return;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;
        const py = (e.clientY - r.top) / r.height;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          el.style.setProperty("--rx", `${((0.5 - py) * max * 2).toFixed(2)}deg`);
          el.style.setProperty("--ry", `${((px - 0.5) * max * 2).toFixed(2)}deg`);
          el.style.setProperty("--gx", `${(px * 100).toFixed(1)}%`);
          el.style.setProperty("--gy", `${(py * 100).toFixed(1)}%`);
        });
      });
      el.addEventListener("pointerleave", () => { cancelAnimationFrame(frame); reset(); });
    });
  }

  /* ---------------------------------------------------------------- hero --- */
  function initHero(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const slides = $$("[data-sttr-slide]", el);
    const dots = $$("[data-sttr-hero-dot]", el);
    const n = slides.length;
    if (n < 2) return;
    const interval = Number(el.dataset.interval) || 7000;
    let index = 0;
    let timer = 0;
    const show = (i) => {
      index = (i + n) % n;
      slides.forEach((s, k) => { s.classList.toggle("is-on", k === index); s.setAttribute("aria-hidden", String(k !== index)); });
      dots.forEach((d, k) => { d.classList.toggle("is-active", k === index); if (k === index) d.setAttribute("aria-current", "true"); else d.removeAttribute("aria-current"); });
      restart();
    };
    const restart = () => {
      clearInterval(timer);
      if (reduce()) return;
      timer = setInterval(() => show(index + 1), interval);
    };
    const prev = $("[data-sttr-hero-prev]", el);
    const next = $("[data-sttr-hero-next]", el);
    if (prev) prev.addEventListener("click", () => show(index - 1));
    if (next) next.addEventListener("click", () => show(index + 1));
    dots.forEach((d) => d.addEventListener("click", () => show(Number(d.dataset.sttrHeroDot))));
    let drag = { x: 0, y: 0, lock: "" };
    el.addEventListener("pointerdown", (e) => { drag = { x: e.clientX, y: e.clientY, lock: "" }; });
    el.addEventListener("pointermove", (e) => {
      if (e.buttons !== 1 || drag.lock) return;
      const dx = e.clientX - drag.x; const dy = e.clientY - drag.y;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) drag.lock = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    });
    el.addEventListener("pointerup", (e) => {
      if (drag.lock !== "x") return;
      const dx = e.clientX - drag.x;
      if (dx > 48) show(index - 1); else if (dx < -48) show(index + 1);
    });
    document.addEventListener("visibilitychange", () => { if (document.hidden) clearInterval(timer); else restart(); });
    restart();
  }

  /* ----------------------------------------------------------- coverflow --- */
  function initCoverflow(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const size = el.dataset.size || "piece";
    const scene = $("[data-sttr-cover-scene]", el);
    const cards = $$("[data-sttr-cover-card]", el);
    const dots = $$("[data-sttr-cover-dot]", el);
    const counter = $("[data-sttr-cover-counter]", el);
    const n = cards.length;
    if (!scene || n === 0) return;
    if (reduce()) {
      scene.classList.remove("cover-scene", `is-${size}`);
      scene.classList.add("cover-rail", `is-${size}`);
      cards.forEach((c) => { c.classList.remove("cover-card", `is-${size}`); c.classList.add("is-active"); });
      $$("[data-sttr-cover-prev], [data-sttr-cover-next]", el).forEach((b) => { b.hidden = true; });
      return;
    }
    const shift = size === "feature" ? 118 : size === "panel" ? 70 : 64;
    const depth = size === "feature" ? 28 : size === "panel" ? 110 : 130;
    const turn = size === "feature" ? 16 : size === "panel" ? 32 : 36;
    const dim = size === "feature" ? 0.1 : 0.22;
    let index = 0;
    let paused = false;
    let onscreen = true;
    let timer = 0;
    const render = () => {
      cards.forEach((card, i) => {
        let off = i - index;
        if (off > n / 2) off -= n;
        if (off < -n / 2) off += n;
        const abs = Math.abs(off);
        card.style.transform = `translateX(${off * shift}%) translateZ(${-abs * depth}px) rotateY(${off * -turn}deg)`;
        card.style.opacity = abs > 2 ? "0" : String(1 - abs * dim);
        card.style.zIndex = String(12 - abs);
        card.style.pointerEvents = abs > 2 ? "none" : "auto";
        card.classList.toggle("is-active", off === 0);
      });
      dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
      if (counter) counter.textContent = `${pad(index + 1)}/${pad(n)}`;
    };
    const go = (i) => { index = (i + n) % n; render(); restart(); };
    const restart = () => {
      clearInterval(timer);
      if (paused || !onscreen || n < 2) return;
      timer = setInterval(() => { index = (index + 1) % n; render(); }, 4600);
    };
    el.addEventListener("mouseenter", () => { paused = true; restart(); });
    el.addEventListener("mouseleave", () => { paused = false; restart(); });
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(([entry]) => { onscreen = Boolean(entry && entry.isIntersecting); restart(); }, { threshold: 0.25 }).observe(el);
    }
    let drag = { x: 0, y: 0, lock: "", moved: 0 };
    el.addEventListener("pointerdown", (e) => { drag = { x: e.clientX, y: e.clientY, lock: "", moved: 0 }; });
    el.addEventListener("pointermove", (e) => {
      if (e.buttons !== 1) return;
      const dx = e.clientX - drag.x; const dy = e.clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.abs(dx));
      if (!drag.lock && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) drag.lock = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    });
    el.addEventListener("pointerup", (e) => {
      if (drag.lock !== "x") return;
      const dx = e.clientX - drag.x;
      if (dx > 48) go(index - 1); else if (dx < -48) go(index + 1);
    });
    el.addEventListener("click", (e) => { if (drag.moved > 12) { e.preventDefault(); e.stopPropagation(); } }, true);
    $$("[data-sttr-cover-prev], [data-sttr-cover-next], [data-sttr-cover-dot]", el).forEach((b) => b.addEventListener("pointerdown", (e) => e.stopPropagation()));
    const prev = $("[data-sttr-cover-prev]", el);
    const next = $("[data-sttr-cover-next]", el);
    if (prev) prev.addEventListener("click", () => go(index - 1));
    if (next) next.addEventListener("click", () => go(index + 1));
    dots.forEach((d) => d.addEventListener("click", () => go(Number(d.dataset.sttrCoverDot))));
    render();
    restart();
  }

  /* -------------------------------------------------- collection filters --- */
  function initCollection(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const grid = $("[data-sttr-grid]", el);
    if (!grid) return;
    const low = Number(el.dataset.priceLow) || 15000;
    const high = Number(el.dataset.priceHigh) || 17000;
    const cards = $$("[data-sttr-card]", grid).map((node) => ({
      node,
      title: node.dataset.title || "",
      text: `${node.dataset.title || ""} ${node.dataset.text || ""}`.toLowerCase(),
      price: Number(node.dataset.price) || 0,
      tags: (node.dataset.tags || "").split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
      collections: (node.dataset.collections || "").split(",").map((t) => t.trim()).filter(Boolean),
      featured: node.dataset.featured === "true",
      stone: node.dataset.stone === "true",
    }));
    const inputs = $$("[data-sttr-filter]", el);
    const count = $("[data-sttr-count]", el);
    const countWrap = $("[data-sttr-count-wrap]", el);
    const empty = $("[data-sttr-empty]", el);
    const request = $("[data-sttr-request]", el);
    const title = $("[data-sttr-title]", el);
    const intro = $("[data-sttr-intro]", el);
    const defaults = { title: title ? title.textContent : "", intro: intro ? intro.textContent : "" };
    const input = (name) => inputs.find((x) => x.dataset.sttrFilter === name);
    /* A category opened from the menu gets its own title and intro (ported from AllShop). */
    const syncHeading = (option) => {
      const own = option && option.value !== "all";
      if (title) title.textContent = own ? option.dataset.label || option.textContent.trim() : defaults.title;
      if (intro) {
        const text = own && option.dataset.intro ? option.dataset.intro : defaults.intro;
        intro.textContent = text;
        intro.hidden = !text.trim();
      }
    };
    const readGroupParam = () => {
      try { return (new URLSearchParams(window.location.search).get("group") || "").toLowerCase(); } catch { return ""; }
    };
    const selectGroup = (groupSelect, wanted) => {
      const value = wanted && Array.from(groupSelect.options).some((o) => o.value === wanted) ? wanted : "all";
      groupSelect.value = value;
      return groupSelect.options[groupSelect.selectedIndex];
    };
    const syncUrl = (value) => {
      try {
        const url = new URL(window.location.href);
        if (value === "all") url.searchParams.delete("group"); else url.searchParams.set("group", value);
        if (url.href !== window.location.href) history.pushState({ sttrGroup: value }, "", url.href);
      } catch { /* ignore */ }
    };
    const value = (name) => { const i = input(name); return i ? i.value : ""; };
    /* A category option carries its rules as data attributes; every filled rule must hold. */
    const inGroup = (card, option) => {
      if (!option || option.value === "all") return true;
      const d = option.dataset;
      const title = card.title.toLowerCase();
      if (d.collection && !card.collections.includes(d.collection)) return false;
      if (d.tag && !card.tags.includes(d.tag)) return false;
      if (d.contains && !title.includes(d.contains)) return false;
      if (d.excludes && title.includes(d.excludes)) return false;
      return true;
    };
    /* Links such as /collections/all?group=chains&q=moon preselect the filters. */
    try {
      const params = new URLSearchParams(window.location.search);
      const groupSelect = input("group");
      if (groupSelect) syncHeading(selectGroup(groupSelect, readGroupParam()));
      const qInput = input("q");
      if (qInput && params.get("q") && !qInput.value) qInput.value = params.get("q");
    } catch { /* ignore */ }
    const apply = () => {
      const q = value("q").trim().toLowerCase();
      const groupSelect = input("group");
      const groupOption = groupSelect ? groupSelect.options[groupSelect.selectedIndex] : null;
      const price = value("price") || "all";
      const stone = value("stone") || "all";
      const sort = value("sort") || "featured";
      const byRequest = !!(groupOption && groupOption.dataset.request === "true");
      let shown = byRequest ? [] : cards.filter((c) => {
        if (!inGroup(c, groupOption)) return false;
        if (q && !c.text.includes(q)) return false;
        if (stone === "stone" && !c.stone) return false;
        if (stone === "plain" && c.stone) return false;
        if (price === "under" && c.price >= low) return false;
        if (price === "mid" && (c.price < low || c.price > high)) return false;
        if (price === "over" && c.price <= high) return false;
        return true;
      });
      shown = shown.slice().sort((a, b) => {
        if (sort === "price-asc") return a.price - b.price;
        if (sort === "price-desc") return b.price - a.price;
        if (sort === "name") return a.title.localeCompare(b.title);
        return Number(b.featured) - Number(a.featured) || a.title.localeCompare(b.title);
      });
      const visible = new Set(shown.map((c) => c.node));
      cards.forEach((c) => { c.node.style.display = visible.has(c.node) ? "" : "none"; });
      shown.forEach((c) => grid.appendChild(c.node));
      if (count) count.textContent = String(shown.length);
      if (countWrap) countWrap.classList.toggle("st:hidden", byRequest);
      if (request) request.classList.toggle("st:hidden", !byRequest);
      if (empty) empty.classList.toggle("st:hidden", byRequest || shown.length > 0);
      grid.classList.toggle("st:hidden", byRequest || shown.length === 0);
    };
    inputs.forEach((i) => i.addEventListener(i.tagName === "SELECT" ? "change" : "input", apply));
    const groupSelect = input("group");
    if (groupSelect) {
      groupSelect.addEventListener("change", () => {
        syncUrl(groupSelect.value);
        syncHeading(groupSelect.options[groupSelect.selectedIndex]);
      });
      window.addEventListener("popstate", () => { syncHeading(selectGroup(groupSelect, readGroupParam())); apply(); });
    }
    apply();
  }

  /* ------------------------------------------------- save hearts on cards --- */
  function paintWishToggles(scope) {
    const list = storage.get("sttr-wish", []);
    $$("[data-sttr-wish-toggle]", scope).forEach((b) => {
      const on = list.includes(b.dataset.handle);
      b.setAttribute("aria-pressed", String(on));
      b.setAttribute("aria-label", on ? b.dataset.done || "Remove from saved" : b.dataset.label || "Save");
    });
  }
  function initWishToggles(scope) {
    $$("[data-sttr-wish-toggle]", scope).forEach((b) => {
      if (b.dataset.init) return;
      b.dataset.init = "1";
      b.addEventListener("click", () => {
        const handle = b.dataset.handle;
        if (!handle) return;
        const list = storage.get("sttr-wish", []);
        storage.set("sttr-wish", list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
        paintWishToggles(document);
      });
    });
    paintWishToggles(scope);
  }

  /* ------------------------------------------------------------- product --- */
  function initProduct(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const form = $("[data-sttr-form]", el) || $("#sttr-product-form", el);
    const idInput = $("[data-sttr-variant-id]", el);
    const data = $("[data-sttr-variants]", el);
    let variants = [];
    try { variants = JSON.parse(data ? data.textContent : "[]"); } catch { variants = []; }
    const groups = $$("[data-sttr-option]", el);
    const prices = $$("[data-sttr-price]", el);
    const buttons = $$("[data-sttr-add-button]", el);
    const label = $("[data-sttr-selected-label]", el);
    const current = () => variants.find((v) => String(v.id) === String(idInput && idInput.value)) || variants[0];
    let selected = current() ? current().options.slice() : [];

    /* Photo gallery: thumbnails switch the main photo; a variant with its own photo jumps to it. */
    const slides = $$("[data-sttr-gallery-slide]", el);
    const thumbs = $$("[data-sttr-gallery-thumb]", el);
    const showSlide = (i) => {
      if (i < 0 || i >= slides.length) return;
      slides.forEach((s, k) => s.classList.toggle("is-on", k === i));
      thumbs.forEach((t, k) => { t.classList.toggle("is-active", k === i); t.setAttribute("aria-pressed", String(k === i)); });
    };
    thumbs.forEach((t) => t.addEventListener("click", () => showSlide(Number(t.dataset.sttrGalleryThumb))));
    if (slides.length > 1) {
      const stage = slides[0].parentElement;
      let sx = 0; let sy = 0;
      stage.addEventListener("pointerdown", (e) => { sx = e.clientX; sy = e.clientY; });
      stage.addEventListener("pointerup", (e) => {
        const dx = e.clientX - sx; const dy = e.clientY - sy;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
        const at = slides.findIndex((s) => s.classList.contains("is-on"));
        showSlide((at + (dx < 0 ? 1 : -1) + slides.length) % slides.length);
      });
    }
    let userPicked = false;

    const reflect = (variant) => {
      if (!variant) return;
      if (userPicked && variant.featured_image && variant.featured_image.id) {
        const at = slides.findIndex((s) => s.dataset.imageId === String(variant.featured_image.id));
        if (at >= 0) showSlide(at);
      }
      if (idInput) idInput.value = variant.id;
      prices.forEach((p) => { p.textContent = money(variant.price); });
      buttons.forEach((b) => {
        b.disabled = !variant.available;
        b.textContent = variant.available ? b.dataset.label : b.dataset.sold;
      });
      if (label) label.textContent = variant.title === "Default Title" ? "" : variant.title;
      groups.forEach((g, gi) => {
        $$("[data-sttr-chip]", g).forEach((chip) => {
          const active = chip.dataset.value === selected[gi];
          chip.classList.toggle("is-active", active);
          chip.setAttribute("aria-pressed", String(active));
          const possible = variants.some((v) => v.available && v.options[gi] === chip.dataset.value && v.options.every((o, k) => k === gi || o === selected[k]));
          chip.classList.toggle("is-unavailable", !possible);
        });
      });
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState({}, "", url);
      } catch { /* ignore */ }
    };
    groups.forEach((g, gi) => {
      $$("[data-sttr-chip]", g).forEach((chip) => chip.addEventListener("click", () => {
        userPicked = true;
        selected[gi] = chip.dataset.value;
        let variant = variants.find((v) => v.options.every((o, k) => o === selected[k]));
        if (!variant) {
          variant = variants.find((v) => v.options[gi] === chip.dataset.value && v.available) || variants.find((v) => v.options[gi] === chip.dataset.value);
          if (variant) selected = variant.options.slice();
        }
        reflect(variant);
      }));
    });

    const giftToggle = $("[data-sttr-gift-toggle]", el);
    const giftNote = $("[data-sttr-gift-note]", el);
    if (giftToggle && giftNote) {
      giftToggle.addEventListener("change", () => {
        giftNote.classList.toggle("st:hidden", !giftToggle.checked);
        giftNote.disabled = !giftToggle.checked;
        if (giftToggle.checked) giftNote.focus();
      });
    }

    if (form) {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const submitter = e.submitter && e.submitter.matches("[data-sttr-add-button]") ? e.submitter : buttons[0];
        buttons.forEach((b) => { b.disabled = true; });
        try {
          const fd = new FormData(form);
          if (giftNote && giftNote.disabled) fd.delete(giftNote.name);
          await cartAdd(fd);
          buttons.forEach((b) => flash(b, b.dataset.done || "Added to bag", 2200));
        } catch (err) {
          if (submitter) flash(submitter, err.message || "Try again", 2600);
        } finally {
          setTimeout(() => { const v = current(); buttons.forEach((b) => { b.disabled = !(v && v.available); }); }, 2200);
        }
      });
    }

    const wish = $("[data-sttr-wish]", el);
    const handle = el.dataset.handle;
    if (wish && handle) {
      const paint = () => { const on = storage.get("sttr-wish", []).includes(handle); wish.textContent = on ? wish.dataset.done : wish.dataset.label; wish.setAttribute("aria-pressed", String(on)); };
      wish.addEventListener("click", () => {
        const list = storage.get("sttr-wish", []);
        storage.set("sttr-wish", list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
        paint();
      });
      paint();
    }

    if (handle) {
      const seen = storage.get("sttr-seen", []).filter((s) => s && s.handle !== handle);
      const others = seen.slice(0, 3);
      storage.set("sttr-seen", [{ handle, title: el.dataset.title || handle }, ...seen].slice(0, 8));
      const recent = $("[data-sttr-recent]", el);
      const list = $("[data-sttr-recent-list]", el);
      if (recent && list && others.length) {
        list.textContent = others.map((s) => s.title || s.handle.replace(/-/g, " ")).join(", ");
        recent.classList.remove("st:hidden");
      }
    }
    reflect(current());
  }

  /* ---------------------------------------------------------------- cart --- */
  function initCart(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const sectionId = el.dataset.sectionId;
    const change = async (line, quantity) => {
      el.setAttribute("aria-busy", "true");
      try {
        const res = await fetch(`${routes.cartChange}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ line, quantity, sections: sectionId }),
        });
        const json = await res.json();
        const html = json.sections && json.sections[sectionId];
        if (html) {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const fresh = $("[data-sttr-cart]", doc);
          if (fresh) { el.innerHTML = fresh.innerHTML; }
        }
        if (typeof json.item_count === "number") setCartCount(json.item_count);
      } catch {
        window.location.reload();
      } finally {
        el.removeAttribute("aria-busy");
      }
    };
    el.addEventListener("click", (e) => {
      const qty = e.target.closest("[data-sttr-qty]");
      const remove = e.target.closest("[data-sttr-remove]");
      const row = e.target.closest("[data-sttr-line]");
      if (!row || (!qty && !remove)) return;
      e.preventDefault();
      const line = Number(row.dataset.sttrLine);
      if (remove) { change(line, 0); return; }
      const valueEl = $("[data-sttr-qty-value]", row);
      const currentQty = Number(valueEl ? valueEl.textContent : 1) || 1;
      change(line, Math.max(0, currentQty + Number(qty.dataset.sttrQty)));
    });
  }

  /* -------------------------------------------------------- worn gallery --- */
  function initWorn(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const track = $("[data-sttr-worn-track]", el);
    if (!track) return;
    const prev = $("[data-sttr-worn-prev]", el);
    const next = $("[data-sttr-worn-next]", el);
    const counter = $("[data-sttr-worn-counter]", el);
    const progress = $("[data-sttr-worn-progress]", el);
    const n = track.children.length;
    const step = () => {
      const first = track.children[0];
      return first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || "0") : track.clientWidth;
    };
    const measure = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
      const current = Math.min(n - 1, Math.round(track.scrollLeft / Math.max(1, step())));
      if (counter) counter.textContent = `${pad(current + 1)}/${pad(n)}`;
      if (progress) progress.style.width = `${((current + 1) / n) * 100}%`;
    };
    const go = (dir) => track.scrollBy({ left: dir * step(), behavior: reduce() ? "auto" : "smooth" });
    if (prev) prev.addEventListener("click", () => go(-1));
    if (next) next.addEventListener("click", () => go(1));
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    });
    track.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    measure();
  }

  /* --------------------------------------------------------------- saved --- */
  const productJson = async (handle) => {
    const res = await fetch(`${root}products/${handle}.js`, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(String(res.status));
    return res.json();
  };
  const productImage = (p, width) => (p.featured_image ? `${p.featured_image}${p.featured_image.includes("?") ? "&" : "?"}width=${width}` : "");

  async function initSaved(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const grid = $("[data-sttr-saved-grid]", el);
    const empty = $("[data-sttr-saved-empty]", el);
    if (!grid) return;
    const labels = { add: el.dataset.addLabel || "Add to bag", added: el.dataset.addedLabel || "Added", options: el.dataset.optionsLabel || "Choose options", remove: el.dataset.removeLabel || "Remove" };
    const render = async () => {
      const handles = storage.get("sttr-wish", []);
      if (!handles.length) {
        grid.innerHTML = "";
        grid.classList.add("st:hidden");
        grid.classList.remove("st:grid");
        if (empty) empty.classList.remove("st:hidden");
        return;
      }
      const cards = await Promise.all(handles.map(async (handle, i) => {
        try {
          const p = await productJson(handle);
          const url = `${root}products/${p.handle}`;
          const src = productImage(p, 600);
          const img = src ? `<img src="${src}" alt="" width="600" height="750" class="${jewelClass(p.title)} st:h-full st:w-full st:object-cover st:object-center" loading="lazy">` : "";
          const variant = p.variants.find((v) => v.available) || p.variants[0];
          const single = p.variants.length === 1 && variant && variant.title === "Default Title";
          const action = !p.available || !variant
            ? ""
            : single
              ? `<button type="button" class="st:text-[11px] st:tracking-[0.14em] st:uppercase st:underline" data-sttr-add="${variant.id}" data-label="${escapeHtml(labels.add)}" data-done="${escapeHtml(labels.added)}">${escapeHtml(labels.add)}</button>`
              : `<a href="${url}" class="st:text-[11px] st:tracking-[0.14em] st:uppercase st:underline">${escapeHtml(labels.options)}</a>`;
          return `<article class="auth-rise rose st:flex st:h-full st:flex-col st:bg-black" style="animation-delay: ${i * 70}ms"><a href="${url}" class="st:relative st:block st:aspect-[4/5] st:overflow-hidden st:bg-black">${img}</a><div class="st:flex st:flex-1 st:flex-col st:p-3"><a href="${url}" class="st:text-sm">${escapeHtml(p.title)}</a><p class="st:text-sm st:text-ivory/75">${money(p.price)}</p><div class="st:mt-auto st:flex st:items-center st:justify-between st:pt-3">${action}<button type="button" class="st:text-[11px] st:tracking-[0.14em] st:text-mute st:uppercase" data-sttr-unsave="${escapeHtml(handle)}">${escapeHtml(labels.remove)}</button></div></div></article>`;
        } catch { return ""; }
      }));
      const html = cards.filter(Boolean).join("");
      if (!html) { grid.classList.add("st:hidden"); if (empty) empty.classList.remove("st:hidden"); return; }
      grid.innerHTML = html;
      grid.classList.remove("st:hidden");
      grid.classList.add("st:grid");
      if (empty) empty.classList.add("st:hidden");
      initQuickAdd(grid);
    };
    el.addEventListener("click", (e) => {
      const b = e.target.closest("[data-sttr-unsave]");
      if (!b) return;
      storage.set("sttr-wish", storage.get("sttr-wish", []).filter((h) => h !== b.dataset.sttrUnsave));
      render();
    });
    render();
  }

  /* Account page: the small "Saved · n" card with up to three thumbnails. */
  async function initSavedPreview(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const handles = storage.get("sttr-wish", []);
    const count = $("[data-sttr-saved-count]", el);
    const thumbs = $("[data-sttr-saved-thumbs]", el);
    if (count) count.textContent = String(handles.length);
    if (!handles.length || !thumbs) return;
    const imgs = await Promise.all(handles.slice(0, 3).map(async (h) => {
      try { const p = await productJson(h); const src = productImage(p, 160); return src ? `<a href="${root}products/${p.handle}" class="st:block st:h-16 st:w-16 st:overflow-hidden st:bg-black"><img src="${src}" alt="" width="64" height="64" class="${jewelClass(p.title)} st:h-full st:w-full st:object-cover st:object-center"></a>` : ""; } catch { return ""; }
    }));
    const html = imgs.filter(Boolean).join("");
    if (html) thumbs.innerHTML = html;
  }

  /* Show/hide password buttons and the register form's client-side checks. */
  function initAuthForms(scope) {
    $$("[data-sttr-eye]", scope).forEach((btn) => {
      if (btn.dataset.init) return;
      btn.dataset.init = "1";
      btn.addEventListener("click", () => {
        const input = btn.closest(".auth-field") && $("input", btn.closest(".auth-field"));
        if (!input) return;
        const show = input.type === "password";
        input.type = show ? "text" : "password";
        btn.textContent = show ? btn.dataset.hide || "Hide" : btn.dataset.show || "Show";
      });
    });
    $$("[data-sttr-register-form]", scope).forEach((form) => {
      if (form.dataset.init) return;
      form.dataset.init = "1";
      form.addEventListener("submit", (e) => {
        const pw = $("[data-sttr-password]", form);
        const again = $("[data-sttr-password-again]", form);
        const err = $("[data-sttr-password-error]", form);
        if (!pw || !again || !err) return;
        let message = "";
        if (pw.value.length < 8) message = err.dataset.short || "Use at least 8 characters.";
        else if (pw.value !== again.value) message = err.dataset.mismatch || "Those passwords do not match.";
        if (!message) return;
        e.preventDefault();
        err.textContent = message;
        err.classList.remove("st:hidden", "auth-shake");
        void err.offsetWidth;
        err.classList.add("auth-shake");
      });
    });
  }

  /* --------------------------------------------------------------- login --- */
  function initLogin(el) {
    if (el.dataset.init) return;
    el.dataset.init = "1";
    const login = $("[data-sttr-login-form]", el);
    const recover = $("[data-sttr-recover-form]", el);
    if (!login || !recover) return;
    const show = (which) => {
      login.classList.toggle("st:hidden", which === "recover");
      recover.classList.toggle("st:hidden", which !== "recover");
      const field = $("input", which === "recover" ? recover : login);
      if (field) field.focus();
    };
    $$("[data-sttr-show-recover]", el).forEach((a) => a.addEventListener("click", (e) => { e.preventDefault(); history.replaceState({}, "", "#recover"); show("recover"); }));
    $$("[data-sttr-show-login]", el).forEach((a) => a.addEventListener("click", (e) => { e.preventDefault(); history.replaceState({}, "", "#login"); show("login"); }));
    if (window.location.hash === "#recover" || $("[data-sttr-form-errors]", recover)) show("recover");
  }

  /* ---------------------------------------------------------------- boot --- */
  function init(scope) {
    $$("[data-sttr-header]", scope).forEach(initHeader);
    initReveals(scope);
    initTilt(scope);
    initQuickAdd(scope);
    $$("[data-sttr-hero]", scope).forEach(initHero);
    $$("[data-sttr-coverflow]", scope).forEach(initCoverflow);
    $$("[data-sttr-collection]", scope).forEach(initCollection);
    initWishToggles(scope);
    $$("[data-sttr-product]", scope).forEach(initProduct);
    $$("[data-sttr-cart]", scope).forEach(initCart);
    $$("[data-sttr-worn]", scope).forEach(initWorn);
    $$("[data-sttr-saved]", scope).forEach(initSaved);
    $$("[data-sttr-login]", scope).forEach(initLogin);
    $$("[data-sttr-saved-preview]", scope).forEach(initSavedPreview);
    initAuthForms(scope);
  }
  const boot = () => init(document);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:unload", () => { document.body.style.overflow = ""; });
  window.addEventListener("pageshow", (e) => { if (e.persisted) refreshCartCount(); });

  window.STTR = Object.assign(window.STTR || {}, { money, cartAdd, refreshCartCount, init });
})();
